import { type ReactNode } from "react";
import { Typography, Stack, Link, Box } from "@mui/material";
import { useTranslation, type TranslationKey } from "@privacy/translations";
import { ErrorBoundary, type AuthErrorHandler } from "./ErrorBoundary";
import { LayoutWithoutSelectedOrganisation } from "../layout/Layout";
import { isAuthError, useGetHandleAuthError } from "../../auth/AuthProvider";

type AuthConfig =
  { isAuthenticated: true; errorHandler: AuthErrorHandler } | { isAuthenticated: false; errorHandler?: undefined };

type PageErrorBoundaryProps = {
  children?: ReactNode;
  includePlaceholderSidebar: boolean;
  authConfig: AuthConfig;
  authErrorHandler?: AuthErrorHandler;
  homeUrl?: string;
};

export const PageErrorBoundary = ({
  children,
  authConfig,
  includePlaceholderSidebar,
  homeUrl,
}: PageErrorBoundaryProps) => {
  const errorPageProps = {
    isAuthenticated: authConfig.isAuthenticated,
    includePlaceholderSidebar,
    homeUrl,
  } satisfies Partial<ErrorPageProps>;
  return (
    <ErrorBoundary
      authErrorHandler={authConfig.errorHandler}
      statusHandlers={{
        404: ({ error }) => <ErrorNotFound error={error} {...errorPageProps} />,
        403: ({ error }) => <ErrorNotPermitted error={error} {...errorPageProps} />,
        401: ({ error }) => <ErrorNotPermitted error={error} {...errorPageProps} />,
      }}
      unexpectedErrorHandler={error => <ErrorUnexpected error={error} {...errorPageProps} />}
    >
      {children}
    </ErrorBoundary>
  );
};

export const AuthenticatedPageErrorBoundary = ({ children }: { children: ReactNode }) => {
  const handleAuthError = useGetHandleAuthError();
  return (
    <PageErrorBoundary
      authConfig={{ isAuthenticated: true, errorHandler: { handler: handleAuthError, isAuthError: isAuthError } }}
      includePlaceholderSidebar
    >
      {children}
    </PageErrorBoundary>
  );
};

type ErrorPageProps = {
  error?: unknown;
  includePlaceholderSidebar?: boolean;
  isAuthenticated: boolean;
  homeUrl?: string;
};

const ErrorUnexpected = (props: ErrorPageProps) => {
  return <PageErrorWrapper {...props} titleKey="ErrorPage.Unexpected.Title" bodyKey="ErrorPage.Unexpected.Body" />;
};

export const ErrorNotPermitted = (props: ErrorPageProps) => {
  return <PageErrorWrapper {...props} titleKey="ErrorPage.NotPermitted.Title" bodyKey="ErrorPage.NotPermitted.Body" />;
};

export const ErrorNotFound = (props: ErrorPageProps) => {
  return <PageErrorWrapper {...props} titleKey="ErrorPage.NotFound.Title" bodyKey="ErrorPage.NotFound.Body" />;
};

type ErrorWrapperProps = {
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
  error?: unknown;
  includePlaceholderSidebar?: boolean;
  isAuthenticated?: boolean;
  homeUrl?: string;
};

const PageErrorWrapper = ({
  titleKey,
  bodyKey,
  includePlaceholderSidebar = false,
  isAuthenticated = true,
  homeUrl = "/",
}: ErrorWrapperProps) => {
  const { t } = useTranslation();
  const Layout = includePlaceholderSidebar ? LayoutWithoutSelectedOrganisation : Box;

  return (
    <Layout showLogoutButton={isAuthenticated}>
      <Stack
        role="alert"
        sx={{
          flexDirection: "row",
          gap: 10,
          height: "100dvh",
          alignItems: "center",
          px: 5,
          justifyContent: "center",
          transform: "translateY(-100px)",
        }}
      >
        <Box sx={{ display: ["none", "block"] }}>
          <IllustationImage />
        </Box>
        <Stack sx={{ gap: 2, maxWidth: "400px" }}>
          <Typography variant="h1">{t(titleKey)}</Typography>
          <Typography
            sx={{ color: theme => theme.palette.text.tertiary, fontSize: theme => theme.typography.pxToRem(16) }}
          >
            {t(bodyKey)}
          </Typography>
          <Box>
            <Link sx={{ fontSize: theme => theme.typography.pxToRem(16) }} component={"a"} href={homeUrl}>
              {t("ErrorPage.ButtonBackToHomepage")}
            </Link>
          </Box>
        </Stack>
      </Stack>
    </Layout>
  );
};

const IllustationImage = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 527 570" style={{ width: "253px", height: "auto" }}>
      <g fill="#000" fillRule="evenodd">
        <path d="M295.65 0h-65.62v160.24a105.016 105.016 0 0 0 52.508 90.946v.001a104.955 104.955 0 0 0 52.508 14.069 104.95 104.95 0 0 0 52.509-14.07l138.77-80.119-32.81-56.83-191.28 110.436c-.338.197-1.108.643-2.171.643-.642 0-1.392-.164-2.22-.643-2.194-1.267-2.194-3.175-2.194-3.802V0Zm-1 1v219.871c0 2.066.907 3.636 2.693 4.666.89.517 1.805.779 2.72.779 1.335 0 2.309-.566 2.673-.777l190.413-109.936 31.81 55.098-137.904 79.619a104.156 104.156 0 0 1-52.01 13.936 104.155 104.155 0 0 1-52.007-13.936c-32.08-18.521-52.008-53.038-52.008-90.08V1h63.62ZM335.046 304.69c-18.13 0-36.262 4.69-52.508 14.07a105.016 105.016 0 0 0-52.508 90.947v160.24h65.62V349.076c0-.627 0-2.535 2.195-3.802.826-.48 1.576-.643 2.22-.643 1.062 0 1.832.446 2.171.643l191.28 110.436 32.81-56.83-138.772-80.12a104.945 104.945 0 0 0-52.508-14.07m0 1a104.15 104.15 0 0 1 52.008 13.937l137.905 79.619-31.81 55.098L302.74 344.41c-.367-.213-1.34-.78-2.675-.78-.916 0-1.832.263-2.718.778-1.789 1.032-2.696 2.602-2.696 4.668v219.87h-63.62v-159.24c0-37.041 19.928-71.558 52.008-90.08a104.152 104.152 0 0 1 52.008-13.935M32.81 113.535 0 170.364l191.28 110.435c.543.313 2.196 1.268 2.195 3.802.006 2.536-1.65 3.489-2.196 3.803L0 398.839l32.81 56.829 138.772-80.12a105.016 105.016 0 0 0 0-181.893L32.81 113.535Zm.366 1.366 137.906 79.62c32.08 18.52 52.008 53.038 52.008 90.08 0 37.043-19.929 71.56-52.008 90.08l-137.906 79.62-31.81-55.096L191.777 289.27c1.795-1.031 2.703-2.603 2.698-4.67 0-2.064-.906-3.635-2.695-4.668L1.366 169.997l31.81-55.096Z" />
      </g>
    </svg>
  );
};
